import { useState } from 'react'
import { Check } from '@digital-twins/design-system/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { CoverPhotoGalleryProps } from '@/types/components/audiences/review-form'

export function CoverPhotoGallery({
  options,
  selectedIndex,
  onSelect,
  className,
  itemClassName,
}: CoverPhotoGalleryProps) {
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set())

  if (options.length === 0) {
    return (
      <div className={cn('flex gap-4', className)}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className={cn('h-[198px] w-[179px] rounded-lg', itemClassName)} />
        ))}
      </div>
    )
  }

  return (
    <div className={cn('flex gap-4', className)}>
      {options.map((option, index) => {
        const isSelected = index === selectedIndex
        const isLoaded = loadedImages.has(index)
        return (
          <button
            key={option.thumbnailUrl}
            type="button"
            onClick={() => onSelect(index)}
            className={cn(
              'relative h-[198px] w-[179px] overflow-hidden rounded-lg transition-all duration-150',
              itemClassName,
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              isSelected
                ? 'border-2 border-primary shadow-home-card'
                : 'border border-border/50 hover:border-border',
            )}
          >
            {!isLoaded && (
              <Skeleton className="absolute inset-0 rounded-lg" />
            )}
            <img
              src={option.thumbnailUrl}
              alt={`Cover option ${index + 1}`}
              onLoad={() =>
                setLoadedImages((prev) => new Set(prev).add(index))
              }
              className={cn(
                'h-full w-full object-cover transition-opacity duration-300',
                isLoaded ? 'opacity-100' : 'opacity-0',
              )}
            />
            {isSelected && (
              <div className="absolute right-3 top-3 flex h-[26px] w-[26px] items-center justify-center rounded-full bg-primary backdrop-blur-sm">
                <Check className="h-3.5 w-3.5 text-primary-foreground" />
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}
